import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { registerImpersonationGetter } from "@/lib/api";

interface ImpersonationState {
  /** The tenant ID currently being impersonated, or null if not impersonating. */
  targetTenantId: string | null;
  /** Display name of the impersonated tenant. */
  targetTenantName: string | null;
  /** Start impersonating a tenant. Clears React Query cache to prevent data bleed. */
  startImpersonation: (tenantId: string, tenantName: string) => void;
  /** Stop impersonating. Clears React Query cache. */
  stopImpersonation: () => void;
  /** Whether currently impersonating a tenant. */
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationState | null>(null);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [targetTenantId, setTargetTenantId] = useState<string | null>(
    () => sessionStorage.getItem("impersonate_tenant_id"),
  );
  const [targetTenantName, setTargetTenantName] = useState<string | null>(
    () => sessionStorage.getItem("impersonate_tenant_name"),
  );
  const queryClient = useQueryClient();

  // Register the impersonation getter so apiFetch can auto-inject X-Target-Tenant-Id
  useEffect(() => {
    registerImpersonationGetter(() => targetTenantId);
  }, [targetTenantId]);

  const startImpersonation = useCallback(
    (tenantId: string, tenantName: string) => {
      // Cancel in-flight requests first to prevent stale data repopulating cache
      queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] !== "superadmin",
      });
      // Clear all non-superadmin query cache to prevent data bleed
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "superadmin",
      });
      sessionStorage.setItem("impersonate_tenant_id", tenantId);
      sessionStorage.setItem("impersonate_tenant_name", tenantName);
      setTargetTenantId(tenantId);
      setTargetTenantName(tenantName);
    },
    [queryClient],
  );

  const stopImpersonation = useCallback(() => {
    queryClient.cancelQueries({
      predicate: (query) => query.queryKey[0] !== "superadmin",
    });
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "superadmin",
    });
    sessionStorage.removeItem("impersonate_tenant_id");
    sessionStorage.removeItem("impersonate_tenant_name");
    setTargetTenantId(null);
    setTargetTenantName(null);
  }, [queryClient]);

  return (
    <ImpersonationContext.Provider
      value={{
        targetTenantId,
        targetTenantName,
        isImpersonating: targetTenantId !== null,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationState {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return ctx;
}
